/**
 * P3-PROPOSAL — PDF core writer v0.2 with EMBEDDED FONTS (CIDFontType2).
 *
 * Builds on the v0.1 minimal writer's determinism + Latin-1/WinAnsi text
 * discipline, but embeds the Wagner brand font (Barlow 400/500/700, TTF
 * subsets from wagnerskis.com's CDN) as CIDFontType2 with TrueType width
 * tables, so the sheet renders in the brand font anywhere — no viewer font
 * substitution, real text layer.
 *
 * Font data is passed at BUILD time (bundled as base64 assets by the
 * bundler); the writer is deterministic: same ops -> same bytes.
 */
import type { PdfOp, PdfImageOp, PdfLinkOp } from './types';

export { A4_W, A4_H } from './types';

// ---------------------------------------------------------------------------
// Font assets (subset TTFs, ~31KB each). Base64 is generated at repo-sync
// time by scripts/gen-font-assets.mjs and written next to this module so the
// runtime carries no fs dependency.
// ---------------------------------------------------------------------------
import { ARIAL_400, ARIAL_700, BARLOW_COND_700 } from './fontAssets';
// 2026-10-22 fix: the embedded fonts are the ARIAL subsets (0328de4 swapped
// Barlow -> Arial) but the CID/width tables were still the BARLOW ones — the
// text layer decoded correctly (CMap built from the same tables) while the
// DRAWN glyphs came out wrong (CID -> wrong glyph index in the Arial program).
// Both tables must come from the SAME font as the embedded TTF.
import { ARIAL_GIDS } from './arialGids';
import { ARIAL_W } from './arialW';
import { BARLOW_COND_GIDS } from './barlowCondGids';
import { BARLOW_COND_W } from './barlowCondW';

export interface EmbeddedFont {
  name: string;                 // /F1 etc.
  regular: { base64: string };
}

const FONTS = {
  // Wagner brand standards (2026): print type = DIN Next Pro; when DIN Next
  // Pro is unavailable, ARIAL is the sanctioned substitute (web/digital rule,
  // applied here for the PDF pipeline). F2 (medium) falls back to regular —
  // the doc says Bold and Regular are the common weights.
  wagner: [
    { key: 'F1', weight: 400, ttf: ARIAL_400 },
    { key: 'F2', weight: 500, ttf: ARIAL_400 },
    { key: 'F3', weight: 700, ttf: ARIAL_700 },
    // F4: Barlow Condensed Bold — the spec-sheet title face (matches the CEO
    // header mockup 2026-10-22: condensed bold, w/cap ≈ 11). Own GID table.
    { key: 'F4', weight: 700, ttf: BARLOW_COND_700, condensed: true },
  ],
} as const;

export type FontSet = keyof typeof FONTS;

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ---------------------------------------------------------------------------
// Content stream compile (ops -> PDF operators), WinAnsi text via simple CMap
// ---------------------------------------------------------------------------

const WINANSI_MAP: Record<number, number> = {
  0x2013: 0x96, 0x2014: 0x97, 0x2018: 0x91, 0x2019: 0x92,
  0x201C: 0x93, 0x201D: 0x94, 0x2022: 0x95, 0x2026: 0x85,
  0x00A9: 0xA9, 0x00AE: 0xAE, 0x00B0: 0xB0,
};

function escText(s: string): string {
  let out = '';
  for (const ch of s) {
    const c = ch.charCodeAt(0);
    if (ch === '\\' || ch === '(' || ch === ')') out += '\\' + ch;
    else if (c in WINANSI_MAP) out += String.fromCharCode(WINANSI_MAP[c]);
    else if (c >= 32 && c <= 255) out += ch;
    else out += '?';
  }
  return out;
}

function fmt(n: number): string {
  const s = n.toFixed(2);
  return s.replace(/\.?0+$/, '') || '0';
}

function col(c: [number, number, number] | undefined): string {
  const v = c ?? [0, 0, 0];
  // 4-decimal precision: 2 decimals turned #00567D into #00567C (0.49 vs
  // 0.4902 — brand-color fidelity bug caught against the 2026 brand doc).
  const f4 = (n: number): string => n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  return `${f4(v[0])} ${f4(v[1])} ${f4(v[2])}`;
}

function fontKeyFor(weight: number, fontOverride?: string): string {
  if (fontOverride === 'condensed') return 'F4';
  if (weight >= 700) return 'F3';
  if (weight >= 500) return 'F2';
  return 'F1';
}

/** WinAnsi byte -> CID for a font weight. CID = the byte itself; the font's
 *  CIDToGIDMap stream (built from ARIAL_GIDS) translates CID -> glyph. */
function cidFor(byte: number, weight: number): number {
  const table = ARIAL_GIDS[weight >= 700 ? 'bold' : weight >= 500 ? 'medium' : 'regular'];
  return table[String(byte)] ?? table['63'] ?? 1; // unknown -> '?' gid, else gid 1
}

/** Encode text as a 2-byte-CID hex string for Identity-H (each CID = GID). */
function cidHex(s: string, weight: number): string {
  let out = '';
  for (const ch of s) {
    const cid = cidFor(ch.charCodeAt(0) & 0xff, weight);
    out += cid.toString(16).padStart(4, '0');
  }
  return out;
}

/** Build the CIDToGIDMap binary stream for a weight: 256 entries, 2 bytes each,
 *  big-endian GID per CID (CID = WinAnsi byte value). */
function cidToGidStream(weight: number): Uint8Array {
  const table = ARIAL_GIDS[weight >= 700 ? 'bold' : weight >= 500 ? 'medium' : 'regular'];
  const out = new Uint8Array(512);
  for (let cid = 0; cid < 256; cid++) {
    const gid = table[String(cid)] ?? 0;
    out[cid * 2] = (gid >> 8) & 0xff;
    out[cid * 2 + 1] = gid & 0xff;
  }
  return out;
}

function hexBytes(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

export function compileContent(ops: PdfOp[]): string {
  const parts: string[] = [];
  let curDash = '';
  let curCap = '';
  let imgIdx = 0; // /Im<N> resource index — one XObject per image op, in order
  for (const o of ops) {
    if (o.op === 'rect') {
      if (o.fill) parts.push(`${col(o.fill)} rg`);
      if (o.stroke) parts.push(`${col(o.stroke)} RG`);
      parts.push(`${fmt(o.lineWidth ?? 1)} w`);
      parts.push(`${fmt(o.x)} ${fmt(o.y)} ${fmt(o.w)} ${fmt(o.h)} re`);
      parts.push(o.fill && o.stroke ? 'B' : o.fill ? 'f' : 'S');
    } else if (o.op === 'path') {
      if (o.fill) parts.push(`${col(o.fill)} rg`);
      if (o.stroke) parts.push(`${col(o.stroke)} RG`);
      parts.push(`${fmt(o.lineWidth ?? 1)} w`);
      // PDF dash syntax: [pattern] phase d — brackets REQUIRED; the old
      // 'a b d' form parsed as [a]-with-phase-b (near-solid rendering).
      const dash = o.dash ? `[${fmt(o.dash[0])} ${fmt(o.dash[1])}] 0 d` : '';
      if (dash !== curDash) { parts.push(dash || '[] 0 d'); curDash = dash; }
      // Dotted style: tiny on-dashes (0.1pt) only read as DOTS with ROUND
      // line caps (the cap adds the dot's bulk). Butt caps otherwise.
      const cap = o.dash ? '1 J' : '0 J';
      if (cap !== curCap) { parts.push(cap); curCap = cap; }
      // Some ops carry a trailing 'S' inside d (legacy double-stroke). With a
      // DASHED stroke the second pass fills the first pass's dash gaps —
      // the line renders SOLID. Strip the trailing 'S'; pdfCore appends the
      // one and only stroke/fill op below.
      const dLines = o.d.length && o.d[o.d.length - 1].trim() === 'S' ? o.d.slice(0, -1) : o.d;
      parts.push(...dLines);
      parts.push(o.fill && o.stroke ? 'B' : o.fill ? 'f' : 'S');
    } else if (o.op === 'image') {
      // JPEG via DCTDecode XObject — the raw file bytes stream verbatim.
      parts.push('q');
      parts.push(`${fmt(o.w)} 0 0 ${fmt(o.h)} ${fmt(o.x)} ${fmt(o.y)} cm`);
      parts.push(`/Im${++imgIdx} Do`);
      parts.push('Q');
    } else if (o.op === 'link') {
      // Link annotations are page-level, not content-stream — nothing to emit.
      continue;
    } else {
      const weight = o.weight ?? 400;
      const hs = (o as { hScale?: number }).hScale ?? 1;
      const font = `/${fontKeyFor(weight, (o as { font?: string }).font)}`;
      const tm = o.rotate90
        ? `0 1 -1 0 ${fmt(o.x)} ${fmt(o.y)} Tm`
        : hs !== 1
          ? `${fmt(hs)} 0 0 1 ${fmt(o.x)} ${fmt(o.y)} Tm`
          : `${fmt(o.x)} ${fmt(o.y)} Td`;
      parts.push('q');
      parts.push(`${col(o.color)} rg`);
      // Identity-H: text as 2-byte CID hex; CID = GID (CIDToGidMap = identity
      // would need CID==GID, so we emit GID as the CID directly).
      const hex = (o as { font?: string }).font === 'condensed'
        ? cidHexGidCondensed(o.text)
        : cidHexGid(o.text, weight);
      parts.push(`BT ${font} ${fmt(o.size)} Tf ${tm} <${hex}> Tj ET`);
      parts.push('Q');
    }
  }
  return parts.join('\n');
}

/** Collect the XObjects (images) and annotations (links) a page's ops need. */
export function pageImages(ops: PdfOp[]): Array<{ jpegBase64: string }> {
  return ops.filter((o): o is PdfImageOp => o.op === 'image')
    .map((o) => ({ jpegBase64: o.jpegBase64 }));
}

export function pageLinks(ops: PdfOp[]): Array<{ rect: [number, number, number, number]; url: string }> {
  return ops.filter((o): o is PdfLinkOp => o.op === 'link')
    .map((o) => ({ rect: [o.x, o.y, o.x + o.w, o.y + o.h] as [number, number, number, number], url: o.url }));
}

/**
 * JPEG dimensions from the SOF0/SOF2 marker — no decode, no re-encode. Scans
 * the marker segments (0xFFC0..0xFFCF excluding 4-byte standalone markers)
 * until the first frame header. Returns 1x1 for malformed input.
 */
export function jpegDimensions(bytes: Uint8Array): { width: number; height: number } {
  let i = 2; // skip SOI
  while (i + 9 < bytes.length) {
    if (bytes[i] !== 0xff) { i++; continue; }
    const marker = bytes[i + 1];
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      // SOF frame header: [len hi][len lo][precision][height hi][height lo][width hi][width lo]
      return {
        height: (bytes[i + 5] << 8) | bytes[i + 6],
        width: (bytes[i + 7] << 8) | bytes[i + 8],
      };
    }
    if (marker === 0xd8 || (marker >= 0xd0 && marker <= 0xd9)) { i += 2; continue; }
    const segLen = (bytes[i + 2] << 8) | bytes[i + 3];
    if (segLen <= 0) return { width: 1, height: 1 };
    i += 2 + segLen;
  }
  return { width: 1, height: 1 };
}

/** CID = the glyph ID for the WinAnsi byte in the subset font. The text is
 *  first mapped to WinAnsi bytes (em-dash -> 0x97 etc.) — masking a raw
 *  Unicode codepoint would corrupt typographic chars (caught by extraction
 *  verification: '•' 0x2022 & 0xff = 0x22 = '"'). */
function cidHexGid(s: string, weight: number): string {
  const gids = ARIAL_GIDS[weight >= 700 ? 'bold' : weight >= 500 ? 'medium' : 'regular'];
  let out = '';
  for (const ch of s) {
    const c = ch.charCodeAt(0);
    const byte = WINANSI_MAP[c] ?? (c >= 32 && c <= 255 ? c : 63); // unmappable -> '?'
    const gid = gids[String(byte)] ?? gids['63'] ?? 1;
    out += gid.toString(16).padStart(4, '0');
  }
  return out;
}

/** Condensed-face variant: CID = GID from the Barlow Condensed Bold table. */
function cidHexGidCondensed(s: string): string {
  const gids = BARLOW_COND_GIDS.bold;
  let out = '';
  for (const ch of s) {
    const c = ch.charCodeAt(0);
    const byte = WINANSI_MAP[c] ?? (c >= 32 && c <= 255 ? c : 63);
    const gid = gids[String(byte)] ?? gids['63'] ?? 1;
    out += gid.toString(16).padStart(4, '0');
  }
  return out;
}

function encodeLatin1(s: string): Uint8Array {
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
  return out;
}

// ---------------------------------------------------------------------------
// Document assembly with embedded CIDFontType2 fonts
// ---------------------------------------------------------------------------

export interface PdfPage { ops: PdfOp[] }

const A4_W = 595.28, A4_H = 841.89;

export function buildPdfWithFonts(pages: PdfPage[], fontSet: FontSet): Uint8Array {
  const fonts = FONTS[fontSet];
  const nPages = pages.length;

  // ---- EXPLICIT object numbering (single source of truth) ----
  // 1: Catalog, 2: Pages
  // Per font (from 3): Type0, CIDFontType2, FontDescriptor, ToUnicode, FontFile2
  // Then sequentially per page: Page, Contents, then 1 XObject per image
  // (streamed via %%JPGDATA%% splice markers), then if the page carries
  // links: an Annots array obj + one /URI action obj per link.
  const NUM = { catalog: 1, pages: 2, fontBase: 3 };
  const fontObjNum = (i: number, off: 0 | 1 | 2 | 3 | 4) => NUM.fontBase + i * 5 + off;
  const fontObjs = fonts.length;
  let next = NUM.fontBase + fontObjs * 5;

  // Pass 1: allocate numbers.
  const pageSpecs = pages.map((pg) => {
    const images = pageImages(pg.ops);
    const links = pageLinks(pg.ops);
    const pageNum = next++;
    const contentNum = next++;
    const imageNums = images.map(() => next++);
    const annotsNum = links.length ? next++ : null;
    const actionNums = links.map(() => next++);
    return { images, links, pageNum, contentNum, imageNums, annotsNum, actionNums };
  });

  // Build objects (index n-1 = object n).
  const objects: string[] = [];
  const put = (num: number, body: string) => { objects[num - 1] = body; };

  put(NUM.catalog, `<< /Type /Catalog /Pages ${NUM.pages} 0 R >>`);
  const kids: string[] = [];
  for (const s of pageSpecs) kids.push(`${s.pageNum} 0 R`);
  put(NUM.pages, `<< /Type /Pages /Kids [${kids.join(' ')}] /Count ${nPages} >>`);

  fonts.forEach((fnt, i) => {
    const type0 = fontObjNum(i, 0);
    const cid = fontObjNum(i, 1);
    const desc = fontObjNum(i, 2);
    const touni = fontObjNum(i, 3);
    const file2 = fontObjNum(i, 4);
    put(type0,
      `<< /Type /Font /Subtype /Type0 /BaseFont /Arial-${fnt.key} /Encoding /Identity-H ` +
      `/DescendantFonts [${cid} 0 R] /ToUnicode ${touni} 0 R >>`);
    put(cid,
      `<< /Type /Font /Subtype /CIDFontType2 /BaseFont /Arial-${fnt.key} ` +
      `/CIDSystemInfo << /Registry (Adobe) /Ordering (Identity) /Supplement 0 >> ` +
      // We emit GID as the CID directly, so CIDToGIDMap = Identity is exact.
      `/FontDescriptor ${desc} 0 R /DW 1000 ${wArray(fnt.key)} /CIDToGIDMap /Identity >>`);
    put(desc,
      `<< /Type /FontDescriptor /FontName /Barlow-${fnt.key} /Flags 32 ` +
      `/FontBBox [-200 -300 1200 1000] /ItalicAngle 0 /Ascent 900 /Descent -200 ` +
      `/CapHeight 700 /StemV 80 /FontFile2 ${file2} 0 R >>`);
    put(touni, toUnicodeStream(fnt.key));
    const bytes = b64ToBytes(fnt.ttf.base64);
    put(file2, `<< /Length ${bytes.length} /Length1 ${bytes.length} >>\nstream\n%%FONTDATA:${fnt.key}%%\nendstream`);
  });

  pageSpecs.forEach((s, i) => {
    const content = compileContent(pages[i].ops);
    const resFonts = fonts.map((fnt, fi) => `/${fnt.key} ${fontObjNum(fi, 0)} 0 R`).join(' ');
    const resXobjs = s.imageNums.length
      ? ` /XObject << ${s.imageNums.map((n, k) => `/Im${k + 1} ${n} 0 R`).join(' ')} >>` : '';
    // Images are consumed in order; content stream references /Im1.. — one
    // XObject per image op, numbered by order of appearance on the page.
    const annots = s.annotsNum
      ? ` /Annots ${s.annotsNum} 0 R` : '';
    put(s.pageNum,
      `<< /Type /Page /Parent ${NUM.pages} 0 R /MediaBox [0 0 ${fmt(A4_W)} ${fmt(A4_H)}] ` +
      `/Resources << /Font << ${resFonts} >>${resXobjs} >> /Contents ${s.contentNum} 0 R${annots} >>`);
    put(s.contentNum,
      `<< /Length ${encodeLatin1(content).length} >>\nstream\n${content}\nendstream`);
    s.images.forEach((im, k) => {
      const bytes = b64ToBytes(im.jpegBase64);
      const dim = jpegDimensions(bytes);
      put(s.imageNums[k],
        `<< /Type /XObject /Subtype /Image /Width ${dim.width} /Height ${dim.height} ` +
        `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode ` +
        `/Length ${bytes.length} >>\nstream\n%%JPGDATA%%\nendstream`);
    });
    if (s.annotsNum) {
      const entries = s.links.map((lk, k) =>
        `<< /Type /Annot /Subtype /Link /Rect [${s.links[k].rect.map(fmt).join(' ')}] ` +
        `/Border [0 0 0] /A ${s.actionNums[k]} 0 R >>`);
      put(s.annotsNum, `[${entries.join(' ')}]`);
      s.links.forEach((lk, k) => {
        put(s.actionNums[k],
          `<< /Type /Action /S /URI /URI (${lk.url.replace(/[()\\]/g, (m) => '\\' + m)}) >>`);
      });
    }
  });

  // ---- Assemble bytes (font binaries spliced at %%FONTDATA%% markers) ----
  const chunks: Array<Uint8Array | string> = [];
  let byteLen = 0;
  const push = (s: string) => { chunks.push(s); byteLen += encodeLatin1(s).length; };

  push('%PDF-1.4\n');
  const offs: number[] = [0];
  // JPEG binaries for image objects, by object number.
  const jpgByObj = new Map<number, Uint8Array>();
  pageSpecs.forEach((s) => {
    s.images.forEach((im, k) => jpgByObj.set(s.imageNums[k], b64ToBytes(im.jpegBase64)));
  });
  for (let i = 0; i < objects.length; i++) {
    offs.push(byteLen);
    const body = objects[i];
    const jpg = jpgByObj.get(i + 1);
    if (jpg) {
      // Image stream: splice the JPEG bytes at the %%JPGDATA%% marker.
      const idx = body.indexOf('%%JPGDATA%%');
      const head = body.slice(0, idx);
      const tail = body.slice(idx + '%%JPGDATA%%'.length);
      push(`${i + 1} 0 obj\n${head}`);
      chunks.push(jpg); byteLen += jpg.length;
      push(tail);
    } else {
      const fm = body.match(/%%FONTDATA:(\w+)%%/);
      if (fm) {
        const key = fm[1];
        const fnt = (fonts as ReadonlyArray<{ key: string; ttf: { base64: string } }>).find((x) => x.key === key)!;
        const bytes = b64ToBytes(fnt.ttf.base64);
        const idx = body.indexOf(`%%FONTDATA:${key}%%`);
        const head = body.slice(0, idx);
        const tail = body.slice(idx + `%%FONTDATA:${key}%%`.length);
        push(`${i + 1} 0 obj\n${head}`);
        chunks.push(bytes); byteLen += bytes.length;
        push(tail);
      } else {
        push(`${i + 1} 0 obj\n${body}\nendobj\n`);
      }
    }
  }

  const xrefStart = byteLen;
  const n = objects.length + 1;
  push(`xref\n0 ${n}\n`);
  push(`0000000000 65535 f \n`);
  for (let i = 1; i < n; i++) push(`${String(offs[i]).padStart(10, '0')} 00000 n \n`);
  push(`trailer\n<< /Size ${n} /Root ${NUM.catalog} 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`);

  const final = new Uint8Array(byteLen);
  let cursor = 0;
  for (const ch of chunks) {
    if (typeof ch === 'string') {
      const b = encodeLatin1(ch);
      final.set(b, cursor); cursor += b.length;
    } else {
      final.set(ch, cursor); cursor += ch.length;
    }
  }
  return final;
}

/** CIDToGIDMap is Identity (CID = GID), so the /W array is keyed by GID. */
function wArray(key: string): string {
  if (key === 'F4') {
    // BARLOW_COND_* tables are keyed by WinAnsi CHARCODE (not GID): join
    // charcode -> gid (GIDS) with charcode -> width (W) so /W is keyed by
    // CID = GID. Keying /W by charcode made glyphs take random widths (the
    // title rendered ~480pt wide and clipped at the page edge).
    const entries: string[] = [];
    for (const [ccStr, w] of Object.entries(BARLOW_COND_W.bold)) {
      const gid = BARLOW_COND_GIDS.bold[ccStr];
      if (gid && gid > 0) entries.push(`${gid} [${w}]`);
    }
    return `/W [${entries.join(' ')}]`;
  }
  // ARIAL_* tables are likewise keyed by WinAnsi CHARCODE: join through
  // ARIAL_GIDS so /W is keyed by CID = GID. Keying /W by the charcode made
  // every glyph render ~2x wide ('Brandon Cole' 78.9pt vs true 42.5pt) —
  // the viewer trusts /W for advances (caught by vision comparison pass).
  const weight = key === 'F3' ? 'bold' : key === 'F2' ? 'medium' : 'regular';
  const ws = ARIAL_W[weight];
  const entries: string[] = [];
  for (const [ccStr, w] of Object.entries(ws)) {
    const gid = ARIAL_GIDS[weight][ccStr];
    if (gid && gid > 0) entries.push(`${gid} [${w}]`);
  }
  return `/W [${entries.join(' ')}]`;
}

/**
 * ToUnicode CMap for a Barlow subset: maps each GID (used as the CID) back to
 * its Unicode codepoint so text extraction works. Built from ARIAL_GIDS
 * (WinAnsi byte -> GID): inverted, GID -> Unicode (the WinAnsi byte value for
 * ASCII, the true codepoint for the mapped typographic chars).
 */
function toUnicodeStream(key: string): string {
  if (key === 'F4') {
    return toUnicodeFromGids(BARLOW_COND_GIDS.bold);
  }
  const weight = key === 'F3' ? 'bold' : key === 'F2' ? 'medium' : 'regular';
  return toUnicodeFromGids(ARIAL_GIDS[weight]);
}

/** Shared CMap body builder: maps each GID back to its Unicode codepoint. */
function toUnicodeFromGids(gids: Record<string, number>): string {
  const winansiBack: Record<number, number> = {
    0x96: 0x2013, 0x97: 0x2014, 0x91: 0x2018, 0x92: 0x2019,
    0x93: 0x201C, 0x94: 0x201D, 0x95: 0x2022, 0x85: 0x2026,
    0xA9: 0x00A9, 0xAE: 0x00AE, 0xB0: 0x00B0,
  };
  const entries: string[] = [];
  for (const [byteStr, gid] of Object.entries(gids)) {
    const byte = Number(byteStr);
    const uni = winansiBack[byte] ?? byte; // ASCII/latin1: identity
    if (gid > 0) {
      const g = gid.toString(16).padStart(4, '0');
      const u = uni.toString(16).padStart(4, '0');
      entries.push(`<${g}> <${g}> <${u}>`); // bfrange triplet: start end base
    }
  }
  const body = `/CIDInit /ProcSet findresource begin
12 dict begin
begincmap
/CIDSystemInfo << /Registry (Adobe) /Ordering (UCS) /Supplement 0 >> def
/CMapName /Adobe-Identity-UCS def
/CMapType 2 def
1 begincodespacerange
<0000> <ffff>
endcodespacerange
${entries.length} beginbfrange
${entries.join('\n')}
endbfrange
endcmap
CMapName currentdict /CMap defineresource pop
end
end`;
  return `<< /Length ${body.length} >>\nstream\n${body}\nendstream`;
}
